import React, { useEffect, useState } from 'react';
import { ButtonTypeEnum } from '../../enums/button-type-enum';
import { SnackbarSeverityEnum } from '../../enums/snackbar-severity-enum';
import CustomButton from '../MUIComponents/CustomButton';
import SnackbarCustom from '../MUIComponents/SnackbarCustom';
import CkEditorComponent from '../CkEditorComponent/CkEditorComponent';
import { Box, IconButton, Typography, useMediaQuery } from '@mui/material';
import SectionLayout from '../../layouts/SectionLayout';
import { useThemeContext } from '../../theme/ThemeContextProvider';
import {
  useCreateOrUpdateCompanyAboutMutation,
  useDeleteCompanyAboutFileMutation,
  useDeleteCompanyAboutMutation,
  useGetMyCompanyQuery, // ✅ Returns full company (about + main + structure)
} from '../../store/apiSlice/companyApi';
import DragAndDropBox from '../MUIComponents/DragAndDropBox/DragAndDropBox';
import { triggerFileInput } from '../../utils/helper/triggerInputHelper';
import { ElementIdEnum } from '../../enums/element-id-enum';
import UploadedFileIconElement from '../Auth/Elements/UploadedFileIconElement';
import { Photo, Video } from '../../interfaces/media.interface';
import { v4 as uuidv4 } from 'uuid';
import { errorHelper } from '../../utils/helper/error-helper';
import Loader from '../MUIComponents/Loader';
import CompanyAboutFilesIcon from '../Icons/CompanyAboutFilesIcon';
import CompanyAboutPdfFile from './CompanyAboutPdfFile/CompanyAboutPdfFile';
import CompanyAboutPhotoFile from './CompanyAboutPhotoFile/CompanyAboutPhotoFile';
import { isSupportedCompanyAboutFileHelper } from '../../utils/helper/file-validation-helper';
import TrashRedIcon from '../Icons/TrashRedIcon';
import useSnackbar from '../../hooks/useSnackBar';

interface FileWithId {
  id: string;
  file: File;
}

const About = () => {
  const { theme } = useThemeContext();
  const mobileView = useMediaQuery(theme.breakpoints.down('md'));

  const { snackbar, showSnackbar, closeSnackbar } = useSnackbar();
  const [about, setAbout] = useState<string>('');
  const [_dragOver, setDragOver] = useState(false);
  const [media, setMedias] = useState<(Photo | Video)[]>([]);
  const [files, setFiles] = useState<FileWithId[]>([]);

  useEffect(() => {
    return () => {
      media.forEach((m) => {
        if (m.fileUrl?.startsWith('blob:')) {
          URL.revokeObjectURL(m.fileUrl);
        }
      });
    };
  }, [media]);

  const {
    data: getMyCompanyResponseData,
    isLoading: getMyCompanyIsLoading,
    refetch,
  } = useGetMyCompanyQuery();

  const [
    createOrUpdateCompanyAbout,
    { isLoading: updateCompanyAboutIsLoading },
  ] = useCreateOrUpdateCompanyAboutMutation();

  const [deleteCompanyAboutFile] = useDeleteCompanyAboutFileMutation();
  const [deleteCompanyAbout] = useDeleteCompanyAboutMutation();

  useEffect(() => {
    if (getMyCompanyResponseData) {
      setAbout(getMyCompanyResponseData.about || '');
      if (getMyCompanyResponseData.fileUrl) {
        setMedias([
          {
            id: getMyCompanyResponseData.id,
            fileName: getMyCompanyResponseData.fileName,
            fileUrl: getMyCompanyResponseData.fileUrl,
            uniqueKey: getMyCompanyResponseData.uniqueKey,
          },
        ]);
      }
    }
  }, [getMyCompanyResponseData]);

  const onDeleteCompanyAboutSubmit = async () => {
    try {
      // Step 1: Delete file from S3 if exists (backend gets uniqueKey from DB)
      if (getMyCompanyResponseData?.uniqueKey) {
        await deleteCompanyAboutFile();
      }

      // Step 2: Clear description text
      await deleteCompanyAbout().unwrap();

      // Step 3: Update local state
      refetch();
      setAbout('');
      setMedias([]);
      setFiles([]);

      showSnackbar(
        'Company about cleared successfully',
        SnackbarSeverityEnum.SUCCESS
      );
    } catch (error) {
      showSnackbar(errorHelper(error), SnackbarSeverityEnum.ERROR);
    }
  };

  const onSubmit = async () => {
    const companyAboutInfoRequest = new FormData();
    companyAboutInfoRequest.append('about', about);

    try {
      if (files.length === 1) {
        companyAboutInfoRequest.append('file', files[0].file);
      }

      // Single endpoint handles both create and update
      await createOrUpdateCompanyAbout(companyAboutInfoRequest).unwrap();
      refetch();

      showSnackbar(
        'Company about updated successfully',
        SnackbarSeverityEnum.SUCCESS
      );
    } catch (error) {
      showSnackbar(errorHelper(error), SnackbarSeverityEnum.ERROR);
    }
  };

  const addFile = async (files: FileList) => {
    media.forEach((m) => {
      if (m.fileUrl?.startsWith('blob:')) {
        URL.revokeObjectURL(m.fileUrl);
      }
    });
    const newFiles = Array.from(files);
    const invalidFiles = newFiles.filter(
      (file) => !isSupportedCompanyAboutFileHelper(file)
    );

    if (invalidFiles.length > 0) {
      showSnackbar(
        'Invalid file type. Only images (JPEG, PNG, WebP) or PDF',
        SnackbarSeverityEnum.ERROR
      );
      return;
    }

    if (newFiles.length > 1) {
      showSnackbar('Only one file allowed', SnackbarSeverityEnum.ERROR);
      return;
    }

    const mapedFiles: Photo[] = [];
    const filesWithIds: FileWithId[] = [];

    newFiles.forEach((file) => {
      const id = uuidv4();
      mapedFiles.push({
        id,
        fileUrl: URL.createObjectURL(file),
      });
      filesWithIds.push({
        id,
        file,
      });
    });

    setFiles(filesWithIds);
    setMedias(mapedFiles);
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files) {
      addFile(event.target.files);
      event.target.value = '';
    }
  };

  const handleDrop = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setDragOver(false);
    if (event.dataTransfer.files) {
      addFile(event.dataTransfer.files);
    }
  };

  const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setDragOver(true);
  };

  const handleDragLeave = () => {
    setDragOver(false);
  };

  const handleFileRemove = async (identifier: string) => {
    const persistedUniqueKey = getMyCompanyResponseData?.uniqueKey;
    const isPersistedFile =
      !!persistedUniqueKey && identifier === persistedUniqueKey;

    if (!isPersistedFile) {
      const mediaToRemove = media.find((m) => m.id === identifier);
      if (mediaToRemove?.fileUrl?.startsWith('blob:')) {
        URL.revokeObjectURL(mediaToRemove.fileUrl);
      }
      setFiles((prevFiles) =>
        prevFiles.filter((fileWithId) => fileWithId.id !== identifier)
      );
      setMedias((prev) => prev.filter((m) => m.id !== identifier));
      return;
    }

    try {
      await deleteCompanyAboutFile();
      showSnackbar(
        `Company's file deleted successfully`,
        SnackbarSeverityEnum.SUCCESS
      );
      setMedias([]);
      setFiles([]);
      //eslint-disable-next-line
    } catch (error) {
      showSnackbar(
        `The deletion of the company's file failed.`,
        SnackbarSeverityEnum.ERROR
      );
    }
  };

  const isPdfFile = (fileName: string) =>
    fileName.toLowerCase().endsWith('.pdf') ||
    (files.length && files[0].file.type === 'application/pdf');

  return (
    <SectionLayout
      display="flex"
      flexDirection="column"
      gap={mobileView ? '28px' : '36px'}
    >
      <Box
        display={'flex'}
        flexDirection={'row'}
        gap={'12px'}
        justifyContent={'space-between'}
        alignItems={'center'}
      >
        <Typography component="h2" variant="h2" mb="12px">
          About company
        </Typography>

        {getMyCompanyResponseData && (
          <IconButton
            onClick={() => onDeleteCompanyAboutSubmit()}
            title="Clear description and file"
          >
            <TrashRedIcon />
          </IconButton>
        )}
      </Box>

      <div>
        {getMyCompanyIsLoading ? (
          <Loader size={30} color="primary" />
        ) : (
          <Box display={'flex'} flexDirection={'column'}>
            {!media.length && (
              <DragAndDropBox
                handleDragOver={handleDragOver}
                handleDragLeave={handleDragLeave}
                handleDrop={handleDrop}
                handleFileChange={handleFileChange}
                triggerFileInput={() =>
                  triggerFileInput(ElementIdEnum.ADD_COMPANY_IMAGE_ABOUT_BUTTON)
                }
                fileInputId={ElementIdEnum.ADD_COMPANY_IMAGE_ABOUT_BUTTON}
                text={
                  <>
                    Drag and drop your file here
                    <br />
                    Only one file allowed. Only images (JPEG, PNG, WebP) or PDF
                    are accepted.
                  </>
                }
                icon={
                  <UploadedFileIconElement>
                    <CompanyAboutFilesIcon active />
                  </UploadedFileIconElement>
                }
              />
            )}
            {!!media.length &&
              media.map((file) =>
                isPdfFile(file.fileUrl) ? (
                  <CompanyAboutPdfFile
                    key={file.id}
                    files={[file]}
                    handleFileRemove={handleFileRemove}
                  />
                ) : (
                  <CompanyAboutPhotoFile
                    key={file.id}
                    files={[file]}
                    handleFileRemove={handleFileRemove}
                  />
                )
              )}
          </Box>
        )}
      </div>

      <SnackbarCustom
        open={snackbar.open}
        onClose={closeSnackbar}
        message={snackbar.message}
        severity={snackbar.severity}
        autoHideDuration={null}
      />

      {/* TODO: Change editor there */}
      <CkEditorComponent
        label=""
        value={about}
        onChange={(data) => setAbout(data)}
      />

      <Box
        display="flex"
        flexDirection={mobileView ? 'column' : 'row'}
        mt="36px"
        gap="22px"
      >
        <CustomButton
          buttonWidth={mobileView ? '100%' : '220px'}
          variantType={ButtonTypeEnum.PRIMARY}
          onClick={() => onSubmit()}
          disabled={getMyCompanyIsLoading || updateCompanyAboutIsLoading}
        >
          {getMyCompanyIsLoading || updateCompanyAboutIsLoading
            ? 'saving...'
            : getMyCompanyResponseData
              ? 'Update Profile'
              : 'save'}
        </CustomButton>
      </Box>
    </SectionLayout>
  );
};

export default About;
